import React from "react";
import { bool, func, instanceOf, shape } from "prop-types";
import { useSelector } from "react-redux";
import GridTable from "pages/_components/GridTable/GridTable";
import Check from "pages/_components/Check";
import CustomText from "pages/_components/CustomText/CustomText";
import FormattedDate from "pages/_components/FormattedDate";
import { echeqPropTypes } from "pages/echeck/echeckPropTypes";
import { findEmisor, checkDisableMultiple, receivedEcheqsStatusActions, statusTagColor } from "util/echecks";
import FormattedAmount from "pages/_components/FormattedAmount";
import Image from "pages/_components/Image";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import useActionByEcheckState from "pages/echeck/hooks/useActionByEcheckState";
import InfoTag from "pages/echeck/components/InfoTag";
import CustomData from "pages/_components/CustomData/CustomData";

const ReceivedEcheckItem = ({ receivedEcheck, addSelectedRow, selectedRows, setDrawer, isDesktop }) => {
    const { options, isExpired } = useActionByEcheckState(receivedEcheqsStatusActions, receivedEcheck, setDrawer);
    const { environmentCuit, echeqFilters: filters } = useSelector((state) => state.echecks);
    const emisor = findEmisor(receivedEcheck, environmentCuit);
    const { emisorRazonSocial, emisorDocumento } = emisor;
    const isDisabled = checkDisableMultiple(receivedEcheck, environmentCuit, filters.status) || isExpired;
    const estado = receivedEcheck.estado.split("-");

    return (
        <>
            <GridTable.Data columnStart={1} rowStart={1} rowWidth={1} className="align-items-center">
                <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                        e.preventDefault();
                        addSelectedRow(receivedEcheck);
                    }}
                    onKeyDown={(e) => {
                        e.preventDefault();
                        addSelectedRow(receivedEcheck);
                    }}>
                    <Check
                        hideLabel
                        key={receivedEcheck.cheque_numero}
                        id={receivedEcheck.cheque_numero}
                        name={receivedEcheck.cheque_numero}
                        disabled={isDisabled}
                        checked={selectedRows.some((element) => element.cheque_numero === receivedEcheck.cheque_numero)}
                    />
                </div>
            </GridTable.Data>
            <GridTable.Data columnStart={2} rowStart={1} rowWidth={1} className="align-items-center">
                {isDesktop ? (
                    <div className="flex flex-nowrap align-items-center">
                        <CustomText text={receivedEcheck.cheque_numero} textType="TEXT" />
                        {receivedEcheck.tenencia_beneficiario_documento === environmentCuit && (
                            <div className=" d-flex align-items-center">
                                <Image src="images/ownershipEcheqs.svg" />
                            </div>
                        )}
                    </div>
                ) : (
                    <CustomData
                        titleText="echecks.number"
                        titleTextType="I18N"
                        valueText={receivedEcheck.cheque_numero}
                        valueTextType="TEXT"
                        icon={
                            receivedEcheck.tenencia_beneficiario_documento === environmentCuit &&
                            "images/ownershipEcheqs.svg"
                        }
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 3 : 2}
                rowStart={isDesktop ? 1 : 2}
                rowWidth={1}
                alignX={isDesktop ? "center" : "flex-start"}>
                {isDesktop ? (
                    <CustomText text={receivedEcheck.cheque_caracter} textType="TEXT" />
                ) : (
                    <CustomData
                        titleText="echecks.character"
                        titleTextType="I18N"
                        valueText={receivedEcheck.cheque_caracter}
                        valueTextType="TEXT"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 4 : 2}
                rowStart={isDesktop ? 1 : 3}
                rowWidth={1}
                alignX={isDesktop ? "center" : "start"}>
                {isDesktop ? (
                    <CustomText text={emisorRazonSocial} textType="TEXT" className="capitalize wrap" toLower />
                ) : (
                    <CustomData
                        titleText="echecks.emisor"
                        titleTextType="I18N"
                        valueText={emisorRazonSocial.toLowerCase()}
                        valueTextType="TEXT"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 5 : 2}
                rowStart={isDesktop ? 1 : 4}
                rowWidth={1}
                alignX={isDesktop ? "center" : "start"}>
                {isDesktop ? (
                    <CustomText text={emisorDocumento} textType="TEXT" className="data-cuit" />
                ) : (
                    <CustomData
                        titleText="echecks.document"
                        titleTextType="I18N"
                        valueText={emisorDocumento}
                        valueTextType="TEXT"
                        valueClass="data-cuit"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 6 : 2}
                rowStart={isDesktop ? 1 : 5}
                rowWidth={1}
                alignX={isDesktop ? "center" : "start"}>
                {isDesktop ? (
                    <CustomText text={receivedEcheck.moneda} textType="TEXT" />
                ) : (
                    <CustomData
                        titleText="echecks.currency"
                        titleTextType="I18N"
                        valueText={receivedEcheck.moneda}
                        valueTextType="TEXT"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 7 : 2}
                rowStart={isDesktop ? 1 : 6}
                rowWidth={1}
                alignX={isDesktop ? "center" : "start"}>
                {isDesktop ? (
                    <FormattedAmount currency={receivedEcheck.emisor_moneda} quantity={receivedEcheck.monto} />
                ) : (
                    <CustomData
                        titleText="echecks.amount"
                        titleTextType="I18N"
                        valueText={
                            <FormattedAmount currency={receivedEcheck.emisor_moneda} quantity={receivedEcheck.monto} />
                        }
                        valueTextType="TEXT"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 8 : 2}
                rowStart={isDesktop ? 1 : 7}
                rowWidth={1}
                alignX={isDesktop ? "center" : "start"}>
                {isDesktop ? (
                    <FormattedDate date={receivedEcheck.fecha_pago} />
                ) : (
                    <CustomData
                        titleText="echecks.datePayed"
                        titleTextType="I18N"
                        valueText={receivedEcheck.fecha_pago}
                        valueTextType="DATE"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data className="tags-wrapper" columnStart={isDesktop ? 9 : 3} rowStart={1} rowWidth={1}>
                {estado.map((tag) => (
                    <InfoTag type="info" message={tag} tagBackground={statusTagColor(receivedEcheck.estado)} />
                ))}
            </GridTable.Data>
            <GridTable.Data columnStart={isDesktop ? 10 : 4} rowStart={1} rowWidth={1} alignY="start">
                {options.length > 0 && !isExpired && (
                    <CustomButton pullRight icon="images/figma/icon/menu-mobile.svg" options={options} />
                )}
            </GridTable.Data>
        </>
    );
};

ReceivedEcheckItem.propTypes = {
    receivedEcheck: shape(echeqPropTypes).isRequired,
    addSelectedRow: func.isRequired,
    selectedRows: instanceOf(Array).isRequired,
    setDrawer: func.isRequired,
    isDesktop: bool.isRequired,
};

export default ReceivedEcheckItem;
